import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import type {
  AppliedMappingFilterIds,
  MarketFilterOption,
} from 'lumen/components/settings/central-mapping/dropdowns/dropdown-mapping-filter';
import type { DropdownMappingMoreActionsOptionType } from 'lumen/components/settings/central-mapping/dropdowns/dropdown-mapping-more-actions';
import type {
  CentralMappingCategory,
  CentralMappingMarketSubscription,
  CentralMappingOrganizationSettings,
} from 'lumen/models';
import { USER_ENGAGEMENT_ELEMENT_IDS } from 'lumen/models/seen-user-engagement-element';
import type MarketSettingsCentralMappingMappingRoute from 'lumen/routes/market/settings/central-mapping/mapping';
import type { SavingStatusResponse } from 'lumen/services/central-mapping/saving';
import {
  type BarBasedOptionId,
  type Breakdown,
  BREAKDOWN_TRANSLATIONS,
  BREAKDOWNS,
  CENTRAL_MAPPING_BAR_BASED_OPTION_ID,
  CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID,
  CENTRAL_MAPPING_HELP_CENTER_LINKS,
  CENTRAL_MAPPING_INCLUDE_ARCHIVED_OPTIONS,
  CENTRAL_MAPPING_LOCAL_STORAGE_KEY_USER_SELECTED_PAGE_SIZE,
  CENTRAL_MAPPING_MORE_ACTIONS_OPTIONS_ID,
  CENTRAL_MAPPING_SORT_DIRECTION_OPTION_ID,
  CENTRAL_MAPPING_VIEW_FILTER_OPTION_ID,
  type CentralMappingFilterQueryParams,
  type CentralMappingMappingEntryExtendedModel,
  type CommissionableOptionId,
  DEFAULT_BREAKDOWN,
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  DEFAULT_VALUE_TYPE,
  FILTER_OPTIONS,
  type FilterOption,
  type MarketMappingFilter,
  type PmsValueType,
  type SortDirectionOptionId,
  VIEW_ID_TO_ANALYTICS_LABEL,
  type ViewFilterOptionId,
} from 'lumen/utils/central-mapping-constants';
import type { ModelFrom } from 'lumen/utils/routes';

export default class MarketSettingsCentralMappingMappingController extends Controller {
  declare model: ModelFrom<MarketSettingsCentralMappingMappingRoute>;

  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare centralMapping: Services['central-mapping'];
  @service('central-mapping/saving')
  declare centralMappingSaving: Services['central-mapping/saving'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intercom: Services['intercom'];
  @service declare intl: Services['intl'];
  @service declare localStorage: Services['local-storage'];
  @service declare router: Services['router'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];
  @service declare userEngagement: Services['user-engagement'];

  @tracked barBased: BarBasedOptionId = CENTRAL_MAPPING_BAR_BASED_OPTION_ID.ALL;
  @tracked brands: string | null = null;
  @tracked breakdown: Breakdown = DEFAULT_BREAKDOWN;
  @tracked categories: string | null = null;
  @tracked chains: string | null = null;
  @tracked commissionable: CommissionableOptionId =
    CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID.ALL;

  @tracked filterOption: FilterOption = FILTER_OPTIONS.ALL;
  @tracked includeArchived: boolean = false; // Passed to the API to include archived mapping entries
  @tracked isShowingReviMappingTypeModal = false;
  @tracked isShowingSegmentationViewModal = false;
  @tracked isShowingUploadModal = false;
  @tracked marketMappingFilters: MarketMappingFilter[] = [];
  @tracked page = DEFAULT_PAGE;

  @tracked pageSize = DEFAULT_PAGE_SIZE;
  @tracked properties: string | null = null;
  @tracked search: string = '';

  @tracked selectedMappingEntries: CentralMappingMappingEntryExtendedModel[] =
    [];
  @tracked selectedRowIds: string[] = [];
  @tracked sortDirection: SortDirectionOptionId =
    CENTRAL_MAPPING_SORT_DIRECTION_OPTION_ID.ASCENDING;
  @tracked valueType: PmsValueType = DEFAULT_VALUE_TYPE;
  @tracked view: ViewFilterOptionId = CENTRAL_MAPPING_VIEW_FILTER_OPTION_ID.ALL;

  clearSelection = (): void => {
    this.setSelectedRowIds([]);
    this.setSelectedMappingEntries([]);
  };

  updateMappingTask = task({ restartable: true }, async (): Promise<void> => {
    const { mapping, breakdown, valueType, pageSize, page, includeArchived } =
      this;

    const dirtyMappingEntries = mapping.filter(
      (mappingEntry) => mappingEntry.isUnsaved,
    );

    const hasSelectedRows = this.selectedMappingEntries.length > 0;

    if (hasSelectedRows) {
      this.clearSelection();
    }

    try {
      const response = await this.centralMapping.updateMappingTask.perform(
        dirtyMappingEntries,
        breakdown,
        valueType,
      );

      let camelizedSavingStatusResponse: SavingStatusResponse = {
        inProgress: false,
        totalSubscriptionsProcessing: null,
        subscriptionsProcessed: null,
      };

      if (response.meta.status.in_progress) {
        camelizedSavingStatusResponse = {
          inProgress: response.meta.status.in_progress,
          totalSubscriptionsProcessing:
            response.meta.status.total_subscriptions_processing,
          subscriptionsProcessed: response.meta.status.subscriptions_processed,
        };
      }

      const filterOptions: CentralMappingFilterQueryParams = {
        orderBy: `value_${this.sortDirection}`,
        view: this.view,
        search: this.search,
      };

      if (this.brands) {
        filterOptions.brands = this.brands;
      }
      if (this.chains) {
        filterOptions.chains = this.chains;
      }
      if (this.properties) {
        filterOptions.properties = this.properties;
      }
      if (this.barBased) {
        filterOptions.barBased = this.barBased;
      }
      if (this.commissionable) {
        filterOptions.commissionable = this.commissionable;
      }
      if (this.categories) {
        filterOptions.categories = this.categories;
      }

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.model.mapping.perform({
        breakdown,
        valueType,
        pageSize,
        page,
        filterOptions,
        includeArchived,
      });

      this.toast.success(
        this.intl.t('settings.central_mapping.hierarchy.toast.success'),
        {
          showIcon: true,
        },
      );

      if (camelizedSavingStatusResponse.inProgress) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.centralMappingSaving.pollSavingStatusTask.perform({
          breakdown,
          valueType,
          initialSavingStatus: camelizedSavingStatusResponse,
          pageSize: this.pageSize,
          page: this.page,
          filterOptions,
          includeArchived: this.includeArchived,
        });
      }
    } catch (error) {
      this.errorTracking.captureException(error);

      // Show retry alert in a different task, otherwise the save button will stay in a loading state while showing the alert.
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.centralMapping.showRetryAlertTask.perform(this.updateMappingTask);
    }
  });

  uploadMappingTask = task(
    { drop: true },
    async (file: File): Promise<void> => {
      const { breakdown, valueType } = this;

      const isConfirmed = await this.alerting.showConfirmWarningAlert({
        title: this.intl.t(
          'settings.central_mapping.upload_mapping.confirmation.title',
        ),
        text: this.intl.t(
          'settings.central_mapping.upload_mapping.confirmation.body',
        ),
        buttons: {
          cancel: {
            text: this.intl.t('general.cancel'),
          },
          confirm: {
            text: this.intl.t('general.confirm'),
          },
        },
      });

      if (!isConfirmed) {
        return;
      }

      this.usageAnalytics.track('Mapping Upload Confirmed', {
        eventTrigger: 'upload-confirmed',
        breakdown,
        valueType,
      });

      try {
        await this.centralMapping.uploadMappingEntries(
          file,
          breakdown,
          valueType,
        );

        this.isShowingUploadModal = false;

        this.usageAnalytics.track('Mapping Upload Succeeded', {
          eventTrigger: 'upload-succeeded',
          breakdown,
          valueType,
        });

        // Start polling for processing status — the existing banner will show automatically
        void this.centralMappingSaving.pollSavingStatusTask.perform({
          breakdown,
          valueType,
          initialSavingStatus: null,
          pageSize: this.pageSize,
          page: this.page,
          filterOptions: {
            orderBy: `value_${this.sortDirection}`,
            view: this.view,
            search: this.search,
            brands: this.brands ?? undefined,
            chains: this.chains ?? undefined,
            properties: this.properties ?? undefined,
            barBased: this.barBased ?? undefined,
            commissionable: this.commissionable ?? undefined,
            categories: this.categories ?? undefined,
          },
          includeArchived: this.includeArchived,
          showSuccessToast: true,
        });
      } catch (error) {
        this.errorTracking.captureException(error);
        this.isShowingUploadModal = false;

        this.usageAnalytics.track('Mapping Upload Failed', {
          eventTrigger: 'upload-failed',
          breakdown,
          valueType,
        });

        this.toast.error(
          this.intl.t('settings.central_mapping.upload_mapping.error'),
          { showIcon: true },
        );
      }
    },
  );

  get appliedFilters(): AppliedMappingFilterIds {
    return {
      barBasedId: this.barBased,
      commissionableId: this.commissionable,
      categoryIds: this.categories?.split(',') ?? [],
      chainIds: this.chains?.split(',') ?? [],
      brandIds: this.brands?.split(',') ?? [],
      propertyIds: this.properties?.split(',') ?? [],
      sortDirectionId: this.sortDirection,
      includeArchivedId: this.includeArchived
        ? CENTRAL_MAPPING_INCLUDE_ARCHIVED_OPTIONS.SHOW
        : CENTRAL_MAPPING_INCLUDE_ARCHIVED_OPTIONS.HIDE,
      viewId: this.view,
      search: this.search,
    };
  }

  get brandOptions(): MarketFilterOption[] {
    const allBrands = this.marketSubscriptions.flatMap(
      (marketSubscription) => marketSubscription.brandsDetails ?? [],
    );
    // Create a map of unique brands by their ID
    const brandsMap = new Map(
      allBrands.map((brand) => [
        brand.id,
        { id: brand.id ?? '', label: brand.name },
      ]),
    );

    // Extract the unique brands from the map
    const uniqueBrands = [...brandsMap.values()];

    // Sort the unique brands by their name
    uniqueBrands.sort((a, b) => a.label.localeCompare(b.label));

    return uniqueBrands;
  }

  get canManageSegmentationView(): boolean {
    return (
      !this.isReadOnly &&
      this.isMarketSegmentBreakdown &&
      Boolean(this.organizationSettings)
    );
  }

  get chainOptions(): MarketFilterOption[] {
    const allChains = this.marketSubscriptions.flatMap(
      (marketSubscription) => marketSubscription.chainsDetails ?? [],
    );
    // Create a map of unique chains by their ID
    const chainsMap = new Map(
      allChains.map((chain) => [
        chain.id,
        { id: chain.id ?? '', label: chain.name },
      ]),
    );

    // Extract the unique chains from the map
    const uniqueChains = [...chainsMap.values()];

    // Sort the unique chains by their name
    uniqueChains.sort((a, b) => a.label.localeCompare(b.label));

    return uniqueChains;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get curentPageSizeValueOption() {
    const pageSizeToUse =
      this.model.mapping.last?.value?.paginationMeta?.pageSize ?? this.pageSize;
    return {
      value: pageSizeToUse,
      label: this.intl.t('settings.central-mapping.mapping.page_no_of_rows', {
        noOfRows: this.intl.formatNumber(pageSizeToUse),
      }),
    };
  }

  get hasChanges(): boolean {
    const { isProcessing } = this.centralMappingSaving;

    if (isProcessing) {
      // BE doesn't include the saved mapping entries in the POST response, because they might already be processing automapping.
      // In that case, they would send partially updated data, which might cause confusion for users.
      // This means that dirty mapping entries won't be marked as "saved" after the POST request.
      // We manually disable the save button while automapping is being processed.
      // Users are also allowed to leave the page while automapping is being processed.
      // This `isProcessing` check is added in this `hasChanges` getter because it impacts both the save button state
      // and the `transitionHandler` in the route that shows a warning popup to discard unsaved changes.
      return false;
    }

    return this.mapping.some((mappingEntry) => mappingEntry.isUnsaved);
  }

  get hierarchy(): CentralMappingCategory[] {
    return this.model.hierarchy.last?.value?.hierarchy ?? [];
  }

  get isDisabled(): boolean {
    const { isProcessing } = this.centralMappingSaving;

    return this.isSaving || isProcessing || this.isReadOnly;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMarketSegmentBreakdown() {
    return this.breakdown === BREAKDOWNS.MARKET_SEGMENT;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPageLoading() {
    return this.model.mapping?.last?.isRunning || this.isSaving;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isReadOnly() {
    return !this.access.hasCentralMappingMarketEditAccess;
  }

  get isSaving(): boolean {
    return this.updateMappingTask.isRunning;
  }

  get isStaffUser(): boolean {
    return this.access.isStaffUser;
  }

  get mapping(): CentralMappingMappingEntryExtendedModel[] {
    return this.model.mapping.last?.value?.mappingEntries ?? [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get mappingPaginationData() {
    return this.model.mapping.lastSuccessful?.value?.paginationMeta;
  }

  get marketSubscriptions(): CentralMappingMarketSubscription[] {
    return this.model.marketSubscriptions.lastSuccessful?.value?.slice() ?? [];
  }

  get moreActionsOptions(): DropdownMappingMoreActionsOptionType[] {
    const options: DropdownMappingMoreActionsOptionType[] = [
      {
        id: CENTRAL_MAPPING_MORE_ACTIONS_OPTIONS_ID.NAVIGATE_TO_HIERARCHY,
        label: this.navigateToHierarchyButtonLabel,
        icon: this.isReadOnly ? undefined : 'edit-pencil',
      },
    ];

    if (this.canManageSegmentationView) {
      options.push({
        id: CENTRAL_MAPPING_MORE_ACTIONS_OPTIONS_ID.SEGMENTATION_VIEW,
        label: this.intl.t(
          'settings.central_mapping.more_actions.segmentation_view',
        ),
        icon: 'settings',
      });
    }

    options.push({
      id: CENTRAL_MAPPING_MORE_ACTIONS_OPTIONS_ID.HELP_CENTER,
      label: this.intl.t('general.help_center'),
      icon: 'book',
      unsafe_isLink: true,
    });

    return options;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get navigateToHierarchyButtonLabel() {
    if (this.isReadOnly) {
      return this.intl.t('settings.central_mapping.view.hierarchy');
    }
    return this.intl.t('settings.central_mapping.edit.hierarchy');
  }

  get organizationSettings(): CentralMappingOrganizationSettings | undefined {
    return this.model.organizationSettings.lastSuccessful?.value ?? undefined;
  }

  get propertyOptions(): MarketFilterOption[] {
    return this.marketSubscriptions.map(({ id, displayName }) => ({
      id: id ?? '',
      label: displayName,
    }));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showUserOnboardingModal() {
    return (
      !this.isPageLoading &&
      !this.isReadOnly &&
      !this.userEngagement.hasSeen(
        USER_ENGAGEMENT_ELEMENT_IDS.CENTRAL_MAPPING_MAPPING_ONBOARDING_MODAL,
      )
    );
  }

  get title(): string {
    const translation = BREAKDOWN_TRANSLATIONS[this.breakdown];
    const mapping = this.intl.t('settings.pms_mapping.mapping');
    const mappingType = translation
      ? `${this.intl.t(translation)}`
      : `${this.intl.t(BREAKDOWN_TRANSLATIONS[BREAKDOWNS.ROOM_TYPE])}`;
    const title = this.intl.t('settings.central_mapping.hierarchy.title', {
      mappingType,
      mapping,
    });

    return title;
  }

  /**
   * Calculates the total number of unique properties that match the currently applied filters.
   */
  get totalFilteredProperties(): number {
    if (this.isPageLoading) {
      return 0;
    }

    const {
      chainIds = [],
      brandIds = [],
      propertyIds = [],
    } = this.appliedFilters;

    // If no filters applied, return the total number of properties directly.
    if (!chainIds.length && !brandIds.length && !propertyIds.length) {
      return this.marketSubscriptions.length;
    }

    const chainIdsSet = new Set(chainIds);
    const brandIdsSet = new Set(brandIds);
    const uniquePropertyIds = new Set(propertyIds);

    this.marketSubscriptions.forEach(({ id, chainsDetails, brandsDetails }) => {
      if (!id) {
        return;
      }

      const matchesChains = chainsDetails?.some((chain) =>
        chainIdsSet.has(chain.id ?? ''),
      );
      const matchesBrands = brandsDetails?.some((brand) =>
        brandIdsSet.has(brand.id ?? ''),
      );

      if (matchesChains || matchesBrands) {
        uniquePropertyIds.add(id);
      }
    });

    return uniquePropertyIds.size;
  }

  @action
  async applyMappingFilters(
    appliedFilters: AppliedMappingFilterIds,
  ): Promise<void> {
    if (this.hasChanges) {
      const canProceedWithAction = await this.handleUnsavedChanges();

      if (!canProceedWithAction) {
        return;
      }
    }

    const {
      sortDirectionId,
      includeArchivedId,
      viewId,
      chainIds,
      brandIds,
      propertyIds,
      search,
      categoryIds,
      barBasedId,
      commissionableId,
    } = appliedFilters;

    if (barBasedId) {
      this.barBased = barBasedId;

      this.usageAnalytics.track('Mapping Data Filtered', {
        eventTrigger: 'value-filter',
        filter: 'bar-based',
      });
    }

    if (commissionableId) {
      this.commissionable = commissionableId;

      this.usageAnalytics.track('Mapping Data Filtered', {
        eventTrigger: 'value-filter',
        filter: 'commissionable',
      });
    }

    if (categoryIds || this.categories) {
      this.categories = categoryIds?.join(',') ?? null;

      this.usageAnalytics.track('Mapping Data Filtered', {
        eventTrigger: 'value-filter',
        filter: 'category',
      });
    }
    if (chainIds || this.chains) {
      this.chains = chainIds?.join(',') ?? null;

      this.usageAnalytics.track('Mapping Data Filtered', {
        eventTrigger: 'value-filter',
        filter: 'chain',
      });
    }

    if (brandIds || this.brands) {
      this.brands = brandIds?.join(',') ?? null;

      this.usageAnalytics.track('Mapping Data Filtered', {
        eventTrigger: 'value-filter',
        filter: 'brand',
      });
    }

    if (propertyIds || this.properties) {
      this.properties = propertyIds?.join(',') ?? null;

      this.usageAnalytics.track('Mapping Data Filtered', {
        eventTrigger: 'value-filter',
        filter: 'property',
      });
    }

    if (sortDirectionId !== this.sortDirection) {
      this.usageAnalytics.track('Mapping Data Sorted', {
        eventTrigger: 'value-sorting',
        sortBy: 'value',
        sortDirection: sortDirectionId,
      });
    }

    if (sortDirectionId) {
      this.sortDirection = sortDirectionId;
    }

    if (includeArchivedId) {
      this.includeArchived =
        includeArchivedId === CENTRAL_MAPPING_INCLUDE_ARCHIVED_OPTIONS.SHOW;
    }

    if (viewId) {
      this.view = viewId;

      if (viewId !== CENTRAL_MAPPING_VIEW_FILTER_OPTION_ID.ALL) {
        this.usageAnalytics.track('Mapping Data Filtered', {
          eventTrigger: 'value-filter',
          filter: VIEW_ID_TO_ANALYTICS_LABEL[viewId],
        });
      }
    }

    this.search = search ?? '';
  }

  @action closeUserOnboardingModal(): void {
    this.userEngagement.markAsSeen(
      USER_ENGAGEMENT_ELEMENT_IDS.CENTRAL_MAPPING_MAPPING_ONBOARDING_MODAL,
    );
  }

  @action
  discardChanges(): void {
    this.mapping.forEach((mappingEntry) => {
      mappingEntry.rollbackAttributes();
      mappingEntry.rollbackRelationships();
    });
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async handleUnsavedChanges() {
    let canProceedWithAction = this.isMarketSegmentBreakdown
      ? await this.centralMapping.showMarketSegmentationUnsavedChangesAlert(
          () => this.onClickSave(),
          this.valueType,
        )
      : await this.centralMapping.showUnsavedChangesAlert(() =>
          this.discardChanges(),
        );

    if (canProceedWithAction && this.isMarketSegmentBreakdown) {
      const isSuccessful = await this.centralMapping.waitForUpdateMappingTask();

      if (!isSuccessful) {
        canProceedWithAction = false;
      }
    }

    return canProceedWithAction;
  }

  @action handleUploadFile(file: File): void {
    void this.uploadMappingTask.perform(file);
  }

  @action
  async onChangePageSize(pageSize: number): Promise<void> {
    if (this.hasChanges) {
      const canProceedWithAction = await this.handleUnsavedChanges();

      if (!canProceedWithAction) {
        return;
      }
    }

    this.localStorage.setItem(
      CENTRAL_MAPPING_LOCAL_STORAGE_KEY_USER_SELECTED_PAGE_SIZE,
      pageSize.toString(),
    );
    this.page = DEFAULT_PAGE;
    this.pageSize = pageSize;

    this.usageAnalytics.track('Mapping Table Row Number Changed', {
      eventTrigger: 'change-row',
      pageSize: this.pageSize,
    });
  }

  @action
  async onClickDiscardChanges(): Promise<void> {
    if (this.hasChanges) {
      await this.centralMapping.showUnsavedChangesAlert(() =>
        this.discardChanges(),
      );

      this.clearSelection();
    }
  }

  @action onClickNavigateToHierarchy(): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.router.legacyTransitionTo(
      'market.settings.central-mapping.hierarchy',
      {
        queryParams: { breakdown: this.breakdown },
      },
    );

    this.usageAnalytics.track('Hierarchy Page Switched', {
      eventTrigger: 'switch-hierarchy',
    });
  }

  @action
  onClickSave(): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.updateMappingTask.perform();
  }

  @action onClickSegmentationView(): void {
    this.isShowingSegmentationViewModal = true;

    this.usageAnalytics.track('Segment View Opened', {
      eventTrigger: 'segment-view',
    });
  }

  @action onClickUploadMapping(): void {
    this.isShowingUploadModal = true;
  }

  @action onCloseReviMappingTypeModal(): void {
    this.isShowingReviMappingTypeModal = false;
  }

  @action onCloseSegmentationViewModal(): void {
    this.isShowingSegmentationViewModal = false;
  }

  @action onCloseUploadModal(): void {
    this.isShowingUploadModal = false;
  }

  @action onRemoveFilterClicked(removedFilter: MarketMappingFilter): void {
    this.marketMappingFilters = this.marketMappingFilters.filter(
      (filter) =>
        !(
          filter.id === removedFilter.id && filter.label === removedFilter.label
        ),
    );
  }

  @action onSegmentationViewSaved(): void {
    // Refetch the mapping so the "--" row is hidden/shown in the rate-code
    // tab per the new fallback-rule value.
    void this.model.refreshMappingEntries();
  }

  @action onSelectMoreActionsOption(
    option: DropdownMappingMoreActionsOptionType,
  ): void {
    switch (option.id) {
      case CENTRAL_MAPPING_MORE_ACTIONS_OPTIONS_ID.NAVIGATE_TO_HIERARCHY:
        this.onClickNavigateToHierarchy();
        break;
      case CENTRAL_MAPPING_MORE_ACTIONS_OPTIONS_ID.SEGMENTATION_VIEW:
        this.onClickSegmentationView();
        break;
      case CENTRAL_MAPPING_MORE_ACTIONS_OPTIONS_ID.HELP_CENTER:
        this.openHelpArticle();
        break;
      default:
        break;
    }
  }

  @action
  async onSetPage(page: number): Promise<void> {
    if (this.hasChanges) {
      const canProceedWithAction = await this.handleUnsavedChanges();

      if (!canProceedWithAction) {
        return;
      }
    }

    this.page = page;
    // Clear selection when changing page
    if (page !== this.mappingPaginationData?.page) {
      this.clearSelection();
    }

    this.usageAnalytics.track('Mapping Page Number Changed', {
      eventTrigger: 'change-pages',
      page: this.page,
    });
  }

  @action openHelpArticle(): void {
    this.intercom.redirectToIntercomHelp(
      CENTRAL_MAPPING_HELP_CENTER_LINKS.MAPPING_PAGE,
    );
  }

  @action resetMappingFilters(): void {
    this.chains = null;
    this.brands = null;
    this.properties = null;
    this.barBased = CENTRAL_MAPPING_BAR_BASED_OPTION_ID.ALL;
    this.commissionable = CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID.ALL;
    this.sortDirection = CENTRAL_MAPPING_SORT_DIRECTION_OPTION_ID.ASCENDING;
    this.view = CENTRAL_MAPPING_VIEW_FILTER_OPTION_ID.ALL;
    this.search = '';
  }

  @action
  setFilterOption(filter: FilterOption): void {
    this.filterOption = filter;
  }

  @action
  setMarketMappingFilters(filters: MarketMappingFilter[]): void {
    this.marketMappingFilters = filters;
  }

  @action setSelectedMappingEntries(
    selectedMappingEntries: CentralMappingMappingEntryExtendedModel[],
  ): void {
    this.selectedMappingEntries = selectedMappingEntries;
  }

  @action
  setSelectedRowIds(selectedRowIds: string[]): void {
    this.selectedRowIds = selectedRowIds;
  }

  @action
  async setValueType(valueType: PmsValueType): Promise<void> {
    if (valueType !== this.valueType && this.hasChanges) {
      const canProceedWithAction = await this.handleUnsavedChanges();

      if (!canProceedWithAction) {
        return;
      }
    }

    this.valueType = valueType;

    // Stop polling the automapping status when the user switches tabs to prevent
    // the polling requests from stacking up when users switch tabs frequently.
    this.centralMappingSaving.stopPollingSavingStatus();
  }
}
